import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Users, MapPin, Briefcase, Loader2, Camera, CameraOff, Building, X, ArrowUpDown, ChevronRight } from 'lucide-react';
import { useKB } from './KBContext';
import MemberSheet, { MemberAvatar } from './MemberSheet';
import OrgSheet from './OrgSheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembersPanel = () => {
  const { password } = useKB();

  // View mode: 'people' (member grid) | 'companies' (company aggregation grid)
  const [viewMode, setViewMode] = useState('people');

  // --- People view state ---
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ total_active: 0, with_photos: 0, countries: [], membership_types: [] });
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [company, setCompany] = useState('');
  const [hasPhoto, setHasPhoto] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // --- Companies view state ---
  const [companies, setCompanies] = useState([]);
  const [companyQ, setCompanyQ] = useState('');
  const [companySort, setCompanySort] = useState('alpha');  // 'alpha' | 'size'
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // --- Shared ---
  const [loading, setLoading] = useState(true);
  const [routeMissing, setRouteMissing] = useState(false);
  const [showOrg, setShowOrg] = useState(false);

  const fetchFilters = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append('password', password);
      const r = await fetch(`${API_URL}/api/kb/members/filters`, { method: 'POST', body: fd });
      if (r.status === 404) { setRouteMissing(true); return; }
      if (r.ok) setFilters(await r.json());
    } catch (e) {
      console.error('[MembersPanel] fetchFilters failed:', e);
    }
  }, [password]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('password', password);
      if (q) fd.append('q', q);
      if (country) fd.append('country', country);
      if (company) fd.append('company', company);
      if (hasPhoto !== null) fd.append('has_photo', hasPhoto);
      fd.append('limit', '1000');
      const r = await fetch(`${API_URL}/api/kb/members`, { method: 'POST', body: fd });
      if (r.status === 404) { setRouteMissing(true); setLoading(false); return; }
      if (r.ok) {
        const data = await r.json();
        // De-dupe by member_id (the email-keyed cache can surface the same
        // person twice) so React keys stay unique.
        const seen = new Set();
        const unique = (data.items || []).filter((m) => {
          const k = m.member_id ?? m.email ?? m.full_name;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setMembers(unique);
        setTotal(data.total);
      }
    } catch (e) {
      console.error('[MembersPanel] fetchMembers failed:', e);
    }
    setLoading(false);
  }, [password, q, country, company, hasPhoto]);

  const fetchCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const fd = new FormData();
      fd.append('password', password);
      if (companyQ) fd.append('q', companyQ);
      fd.append('sort', companySort);
      const r = await fetch(`${API_URL}/api/kb/members/companies`, { method: 'POST', body: fd });
      if (r.ok) {
        const data = await r.json();
        setCompanies(data.items);
      }
    } catch (e) {
      console.error('[MembersPanel] fetchCompanies failed:', e);
    }
    setCompaniesLoading(false);
  }, [password, companyQ, companySort]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => {
    if (viewMode !== 'people') return;
    const id = setTimeout(() => { fetchMembers(); }, 250);
    return () => clearTimeout(id);
  }, [viewMode, fetchMembers]);

  useEffect(() => {
    if (viewMode !== 'companies') return;
    const id = setTimeout(() => { fetchCompanies(); }, 250);
    return () => clearTimeout(id);
  }, [viewMode, fetchCompanies]);

  // Click a company → switch to People view filtered to that company's team
  const drillIntoCompany = (companyName) => {
    if (!companyName) return;
    setCompany(companyName);
    setQ('');
    setCountry('');
    setHasPhoto(null);
    setViewMode('people');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearCompany = () => setCompany('');

  // Render helper for the company card preview avatars
  const CompanyAvatarStack = ({ members: cm = [] }) => {
    if (!cm.length) return null;
    return (
      <div style={{ display: 'flex', marginLeft: 4 }}>
        {cm.slice(0, 3).map((m, i) => (
          <div key={i} style={{ marginLeft: i === 0 ? 0 : -8 }}>
            <MemberAvatar member={m} size={28} />
          </div>
        ))}
      </div>
    );
  };

  // Admin-only membership-tier badge. The backend only sends `membership_tier`
  // to staff/admin viewers (never to logged-in members), so simply rendering
  // when present keeps it admin-gated.
  const TIER_COLORS = {
    Platinum:   { bg: 'rgba(100,116,139,0.14)', fg: '#475569', bd: 'rgba(100,116,139,0.35)' },
    Palladium:  { bg: 'rgba(13,148,136,0.12)',  fg: '#0F766E', bd: 'rgba(13,148,136,0.35)' },
    Gold:       { bg: 'rgba(201,155,67,0.16)',  fg: '#9A7223', bd: 'rgba(201,155,67,0.45)' },
    Silver:     { bg: 'rgba(148,163,184,0.16)', fg: '#5B6675', bd: 'rgba(148,163,184,0.4)' },
    Individual: { bg: 'rgba(37,99,235,0.10)',   fg: '#2563EB', bd: 'rgba(37,99,235,0.28)' },
    Honorary:   { bg: 'rgba(139,92,246,0.12)',  fg: '#7C3AED', bd: 'rgba(139,92,246,0.32)' },
    Associate:  { bg: 'rgba(120,113,108,0.12)', fg: '#6B6560', bd: 'rgba(120,113,108,0.3)' },
    Student:    { bg: 'rgba(22,163,74,0.12)',   fg: '#15803D', bd: 'rgba(22,163,74,0.32)' },
  };
  const TierBadge = ({ tier }) => {
    if (!tier) return null;
    const c = TIER_COLORS[tier] || { bg: 'rgba(120,113,108,0.12)', fg: '#6B6560', bd: 'rgba(120,113,108,0.3)' };
    return (
      <span
        data-testid="kb-member-tier-badge"
        title={`Membership tier: ${tier}`}
        style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
        }}
      >
        {tier}
      </span>
    );
  };

  return (
    <div className="kb-members-panel" data-testid="kb-members-panel">
      <div className="kb-members-header">
        <div>
          <h1 className="kb-members-title">
            {company && viewMode === 'people' ? (
              <>
                <Building size={24} style={{ verticalAlign: -3, marginRight: 8, color: '#5B9AFF' }} />
                {company}
              </>
            ) : (
              <>
                Member Directory
                <span className="kb-members-soon">Phase 3 preview</span>
              </>
            )}
          </h1>
          <p className="kb-members-sub">
            {company && viewMode === 'people' ? (
              <>Team members from <strong>{company}</strong> registered with IPMI.</>
            ) : (
              <>
                Browse the precious metals community. {filters.total_active.toLocaleString()} active members · {filters.with_photos.toLocaleString()} with photos.
              </>
            )}
          </p>
        </div>
      </div>

      {/* People / Companies toggle */}
      <div className="kb-view-toggle" data-testid="kb-view-toggle"
        style={{
          display: 'inline-flex',
          padding: 4,
          background: 'var(--kb-bg-soft, rgba(0,0,0,0.04))',
          border: '1px solid var(--kb-border)',
          borderRadius: 999,
          marginBottom: 18,
          gap: 2,
        }}>
        <button
          type="button"
          data-testid="kb-view-toggle-people"
          onClick={() => setViewMode('people')}
          className={viewMode === 'people' ? 'active' : ''}
          style={{
            padding: '8px 18px',
            borderRadius: 999,
            border: 'none',
            background: viewMode === 'people' ? 'var(--kb-bg, #FFFFFF)' : 'transparent',
            color: viewMode === 'people' ? 'var(--kb-accent, #2563EB)' : 'var(--kb-fg-soft, #8B96A5)',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            letterSpacing: '0.02em',
            boxShadow: viewMode === 'people' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.18s ease',
          }}
        >
          <Users size={14} /> People <span style={{ opacity: 0.55, fontWeight: 500 }}>({filters.total_active})</span>
        </button>
        <button
          type="button"
          data-testid="kb-view-toggle-companies"
          onClick={() => { setViewMode('companies'); setCompany(''); }}
          className={viewMode === 'companies' ? 'active' : ''}
          style={{
            padding: '8px 18px',
            borderRadius: 999,
            border: 'none',
            background: viewMode === 'companies' ? 'var(--kb-bg, #FFFFFF)' : 'transparent',
            color: viewMode === 'companies' ? 'var(--kb-accent, #2563EB)' : 'var(--kb-fg-soft, #8B96A5)',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            letterSpacing: '0.02em',
            boxShadow: viewMode === 'companies' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.18s ease',
          }}
        >
          <Building size={14} /> Companies <span style={{ opacity: 0.55, fontWeight: 500 }}>({companies.length || '—'})</span>
        </button>
      </div>

      {/* Active company filter chip — only on people view */}
      {viewMode === 'people' && company && (
        <div className="kb-company-filter-chip" data-testid="kb-company-filter-chip"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '8px 8px 8px 14px', marginLeft: 12,
            background: 'rgba(91,154,255,0.10)',
            border: '1px solid rgba(91,154,255,0.32)',
            borderRadius: 999,
            color: '#5B9AFF', fontSize: 14, fontWeight: 500,
            marginBottom: 18,
          }}>
          <Building size={14} />
          <span>Filtering by: <strong style={{ color: 'inherit' }}>{company}</strong></span>
          <button onClick={clearCompany}
            data-testid="kb-company-filter-clear"
            aria-label="Clear company filter"
            style={{
              background: 'rgba(91,154,255,0.15)', border: 'none',
              width: 22, height: 22, borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'inherit',
            }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* === PEOPLE VIEW === */}
      {viewMode === 'people' && (
        <>
          <div className="kb-members-controls">
            <div className="kb-members-search">
              <Search size={16} />
              <input
                type="text"
                placeholder={company ? `Search within ${company}...` : 'Search by name, company, role, country...'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="kb-members-search"
              />
            </div>

            <select
              className="kb-members-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="kb-members-country"
              disabled={!!company}
              title={company ? 'Clear company filter to use country filter' : ''}
            >
              <option value="">All countries</option>
              {filters.countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <div className="kb-members-photo-toggle">
              <button className={hasPhoto === null ? 'active' : ''} onClick={() => setHasPhoto(null)}>All</button>
              <button className={hasPhoto === true ? 'active' : ''} onClick={() => setHasPhoto(true)} title="With photo"><Camera size={14} /></button>
              <button className={hasPhoto === false ? 'active' : ''} onClick={() => setHasPhoto(false)} title="Without photo"><CameraOff size={14} /></button>
            </div>

            <div className="kb-members-count">
              <Users size={14} /> {total.toLocaleString()} {company ? 'team members' : 'results'}
            </div>
          </div>

          {/* Pinned IPMI organization card — boards, committees, chapters & staff */}
          {!company && (
            <motion.button
              type="button"
              className="kb-org-card kb-org-card-people"
              data-testid="kb-ipmi-org-card-people"
              onClick={() => setShowOrg(true)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', padding: 16, borderRadius: 14,
                border: '1px solid rgba(37,99,235,0.4)',
                background: 'linear-gradient(135deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02))',
                display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16,
                transition: 'all 0.18s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.14)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                background: '#fff', border: '1px solid var(--kb-border)', padding: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}>
                <img src="/org/ipmi-logo.png" alt="IPMI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--kb-fg)' }}>
                    IPMI Boards, Committees, Chapters &amp; Staff
                  </h3>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.06em', background: 'var(--kb-accent, #2563EB)', color: '#fff',
                  }}>IPMI</span>
                </div>
                <div style={{ marginTop: 3, fontSize: 13, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                  Leadership &amp; governance · Executive Committee, Foundation Board, chapters &amp; staff
                </div>
              </div>
              <ChevronRight size={20} style={{ flexShrink: 0, color: 'var(--kb-accent, #2563EB)' }} />
            </motion.button>
          )}

          {loading && members.length === 0 ? (
            <div className="kb-members-loading"><Loader2 className="spin" size={20} /> Loading…</div>
          ) : (
            <motion.div
              className="kb-members-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="kb-members-grid"
            >
              {members.map((m, idx) => (
                <motion.article
                  key={`${m.member_id || m.full_name}-${idx}`}
                  className="kb-member-card"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.02, 0.6), duration: 0.25 }}
                  onClick={() => setSelectedMember(m)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedMember(m); }}}
                  data-testid="kb-member-card"
                >
                  <MemberAvatar member={m} size={72} />
                  <div className="kb-member-meta">
                    <h3 className="kb-member-name">
                      {m.full_name}
                      {m.is_staff && (
                        <span data-testid="kb-member-staff-badge" style={{
                          marginLeft: 8, padding: '2px 7px', borderRadius: 999, fontSize: 9.5, fontWeight: 700,
                          letterSpacing: '0.05em', verticalAlign: 'middle', textTransform: 'uppercase',
                          background: 'var(--kb-accent, #2563EB)', color: '#fff',
                        }}>IPMI Staff</span>
                      )}
                    </h3>
                    {m.company_name && (
                      <button
                        type="button"
                        className="kb-member-company kb-member-company-btn"
                        data-testid="kb-member-company-link"
                        onClick={(e) => { e.stopPropagation(); drillIntoCompany(m.company_name); }}
                        title={`View all IPMI members from ${m.company_name}`}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          font: 'inherit', color: 'inherit', textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        {m.company_name}
                      </button>
                    )}
                    {m.position_title && (
                      <div className="kb-member-position"><Briefcase size={11} /> {m.position_title}</div>
                    )}
                    {(m.city || m.country) && (
                      <div className="kb-member-location">
                        <MapPin size={11} />
                        {[m.city, m.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {m.membership_tier && (
                      <div style={{ marginTop: 8 }}>
                        <TierBadge tier={m.membership_tier} />
                      </div>
                    )}
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </>
      )}

      {/* === COMPANIES VIEW === */}
      {viewMode === 'companies' && (
        <>
          <div className="kb-members-controls">
            <div className="kb-members-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search companies..."
                value={companyQ}
                onChange={(e) => setCompanyQ(e.target.value)}
                data-testid="kb-companies-search"
              />
            </div>

            <button
              type="button"
              data-testid="kb-companies-sort"
              onClick={() => setCompanySort(companySort === 'alpha' ? 'size' : 'alpha')}
              title="Toggle sort"
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: '1px solid var(--kb-border)',
                borderRadius: 8,
                color: 'var(--kb-fg-soft, #8B96A5)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <ArrowUpDown size={14} />
              {companySort === 'alpha' ? 'Alphabetical' : 'By team size'}
            </button>

            <div className="kb-members-count">
              <Building size={14} /> {companies.length.toLocaleString()} companies
            </div>
          </div>

          {companiesLoading && companies.length === 0 ? (
            <div className="kb-members-loading"><Loader2 className="spin" size={20} /> Loading…</div>
          ) : (
            <motion.div
              className="kb-companies-grid"
              data-testid="kb-companies-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 14,
              }}
            >
              {/* Pinned permanent IPMI organization card */}
              <motion.button
                type="button"
                className="kb-company-card kb-org-card"
                data-testid="kb-ipmi-org-card"
                onClick={() => setShowOrg(true)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'left', cursor: 'pointer', padding: 20, borderRadius: 14,
                  border: '1px solid rgba(37,99,235,0.4)',
                  background: 'linear-gradient(135deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02))',
                  display: 'flex', alignItems: 'center', gap: 16, transition: 'all 0.18s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{
                  width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                  background: '#fff', border: '1px solid var(--kb-border)', padding: 7,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                }}>
                  <img src="/org/ipmi-logo.png" alt="IPMI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--kb-fg)' }}>
                      International Precious Metals Institute
                    </h3>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.06em', background: 'var(--kb-accent, #2563EB)', color: '#fff',
                    }}>IPMI</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                    Leadership, board &amp; committees · The home organization
                  </div>
                </div>
                <ChevronRight size={20} style={{ flexShrink: 0, color: 'var(--kb-accent, #2563EB)' }} />
              </motion.button>

              {companies.map((c, idx) => (
                <motion.button
                  key={`${c.company_name}-${idx}`}
                  type="button"
                  className="kb-company-card"
                  data-testid="kb-company-card"
                  onClick={() => drillIntoCompany(c.company_name)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.012, 0.5), duration: 0.22 }}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: 18,
                    borderRadius: 12,
                    border: '1px solid var(--kb-border)',
                    background: 'var(--kb-bg, #FFFFFF)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    transition: 'all 0.18s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(91,154,255,0.4)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(14,20,32,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        margin: 0,
                        fontFamily: 'var(--kb-font-body)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--kb-fg)',
                        lineHeight: 1.3,
                        wordBreak: 'break-word',
                      }}>
                        {c.company_name}
                      </h3>
                      {c.countries && c.countries.length > 0 && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          marginTop: 6, fontSize: 12,
                          color: 'var(--kb-fg-soft, #8B96A5)',
                        }}>
                          <MapPin size={11} /> {c.countries.slice(0, 3).join(', ')}{c.countries.length > 3 ? ` +${c.countries.length - 3}` : ''}
                        </div>
                      )}
                    </div>
                    <div style={{
                      flexShrink: 0,
                      padding: '4px 10px',
                      background: 'rgba(91,154,255,0.10)',
                      border: '1px solid rgba(91,154,255,0.25)',
                      color: 'var(--kb-accent, #5B9AFF)',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}>
                      {c.member_count} {c.member_count === 1 ? 'member' : 'members'}
                    </div>
                  </div>

                  {c.sample_members && c.sample_members.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CompanyAvatarStack members={c.sample_members} />
                      <span style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                        View team →
                      </span>
                    </div>
                  )}
                </motion.button>
              ))}
            </motion.div>
          )}
        </>
      )}

      {/* Empty states */}
      {routeMissing && (
        <div className="kb-members-empty" data-testid="kb-members-route-missing"
          style={{ background: '#fdf3e7', borderLeft: '3px solid #c0392b', padding: '16px 20px', borderRadius: 4, color: '#7d2d1f', textAlign: 'left' }}>
          <strong>⚠ Member Directory backend not deployed.</strong>
          <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
            The server hosting this app is running an older build that doesn't include the Member Directory feature.
            Please trigger a fresh redeploy from the Emergent platform. After redeploy, members will appear automatically.
          </div>
        </div>
      )}

      {!routeMissing && viewMode === 'people' && members.length === 0 && !loading && filters.total_active === 0 && !q && !country && !company && (
        <div className="kb-members-empty" data-testid="kb-members-needs-sync"
          style={{ background: '#fff8e7', borderLeft: '3px solid #d4a017', padding: '16px 20px', borderRadius: 4, color: '#7a5a10', textAlign: 'left' }}>
          <strong>No members loaded yet.</strong>
          <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
            The directory hasn't been populated. An admin can sync members by opening
            the <strong>Admin Panel → Members &amp; Expertise → Force Glue Up Sync</strong>.
          </div>
        </div>
      )}

      {!routeMissing && viewMode === 'people' && members.length === 0 && !loading && (filters.total_active > 0 || q || country || company) && (
        <div className="kb-members-empty">
          {company ? `No team members found for ${company}.` : 'No members match your search. Try a broader term.'}
        </div>
      )}

      {!routeMissing && viewMode === 'companies' && companies.length === 0 && !companiesLoading && (
        <div className="kb-members-empty">
          {companyQ ? `No companies match "${companyQ}".` : 'No companies in the directory yet.'}
        </div>
      )}

      {showOrg && (
        <OrgSheet
          onClose={() => setShowOrg(false)}
          onMemberClick={(mid) => setSelectedMember({ member_id: mid })}
        />
      )}

      {selectedMember && (
        <MemberSheet
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          onCompanyClick={drillIntoCompany}
        />
      )}
    </div>
  );
};

export default MembersPanel;
